import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { billingAPI, xeroAPI } from "../../lib/api";
import { FREQUENCIES, COST_FREQUENCIES, isOffice365, formatAUD } from "../../lib/billing";
import { Plus, Trash2, Save, X, Monitor } from "lucide-react";
import { toast } from "sonner";

export function BillingProfileEditor({ customer, catalog, licenseTypes, initialServices, onSaved, onCancel, onCatalogRefresh }) {
  const [services, setServices] = useState(() =>
    (initialServices || []).map((s) => ({
      service_name: s.service_name,
      price: String(s.price ?? ""),
      quantity: String(s.quantity ?? 1),
      unit_price: String(s.unit_price ?? (s.price && (s.quantity || 1) ? (parseFloat(s.price) / (parseFloat(s.quantity) || 1)).toFixed(2) : s.price ?? "")),
      ots_cost: String(s.ots_cost ?? ""),
      cost_frequency: s.cost_frequency || "monthly",
      frequency: s.frequency || "monthly",
      renewal_date: s.renewal_date ? String(s.renewal_date).slice(0, 10) : "",
      xero_repeating_invoice_id: s.xero_repeating_invoice_id || null,
      xero_line_item_id: s.xero_line_item_id || null,
      licenses: (s.licenses || []).map((l) => ({
        license_type_name: l.license_type_name,
        quantity: String(l.quantity ?? 1),
        price_per_seat: String(l.price_per_seat ?? ""),
      })),
    }))
  );
  const [selectedCatalogService, setSelectedCatalogService] = useState("");
  const [newServiceName, setNewServiceName] = useState("");
  const [newLicTypes, setNewLicTypes] = useState({});
  const [saving, setSaving] = useState(false);

  const availableCatalog = catalog.filter(
    (c) => !services.some((s) => s.service_name.toLowerCase() === c.name.toLowerCase())
  );

  const addService = (name) => {
    setServices([...services, { service_name: name, price: "", quantity: "1", unit_price: "", ots_cost: "", cost_frequency: "monthly", frequency: "monthly", renewal_date: "", xero_repeating_invoice_id: null, xero_line_item_id: null, licenses: [] }]);
  };

  const addFromCatalog = () => {
    if (!selectedCatalogService) return;
    addService(selectedCatalogService);
    setSelectedCatalogService("");
  };

  const addNewService = async () => {
    const name = newServiceName.trim();
    if (!name) return;
    if (services.some((s) => s.service_name.toLowerCase() === name.toLowerCase())) {
      toast.error("Service already added to this profile");
      return;
    }
    try {
      await billingAPI.addService(name);
      onCatalogRefresh();
      toast.success(`"${name}" saved to services list`);
    } catch (err) {
      // Already in catalog is fine — still add it to the profile
      if (!String(err.response?.data?.detail || "").includes("already exists")) {
        toast.error(err.response?.data?.detail || "Failed to save service");
        return;
      }
    }
    addService(name);
    setNewServiceName("");
  };

  const updateService = (idx, field, value) => {
    const next = [...services];
    next[idx] = { ...next[idx], [field]: value };
    if ((field === "quantity" || field === "unit_price") && next[idx].licenses.length === 0) {
      const q = parseFloat(next[idx].quantity) || 0;
      const u = parseFloat(next[idx].unit_price) || 0;
      next[idx].price = (q * u).toFixed(2);
    }
    setServices(next);
  };

  const stepQty = (idx, delta) => {
    const q = Math.max(1, (parseFloat(services[idx].quantity) || 1) + delta);
    updateService(idx, "quantity", String(q));
  };

  const removeService = (idx) => {
    setServices(services.filter((_, i) => i !== idx));
  };

  const recomputePrice = (svc) => {
    if (svc.licenses.length === 0) return svc.price;
    const total = svc.licenses.reduce(
      (sum, l) => sum + (parseInt(l.quantity) || 0) * (parseFloat(l.price_per_seat) || 0),
      0
    );
    return total.toFixed(2);
  };

  const addLicense = (idx) => {
    const next = [...services];
    next[idx] = {
      ...next[idx],
      licenses: [...next[idx].licenses, { license_type_name: "", quantity: "1", price_per_seat: "" }],
    };
    setServices(next);
  };

  const updateLicense = (sIdx, lIdx, field, value) => {
    const next = [...services];
    const licenses = [...next[sIdx].licenses];
    licenses[lIdx] = { ...licenses[lIdx], [field]: value };
    if (field === "license_type_name") {
      const lt = licenseTypes.find((t) => t.name === value);
      if (lt && lt.default_price > 0 && !licenses[lIdx].price_per_seat) {
        licenses[lIdx].price_per_seat = String(lt.default_price);
      }
    }
    next[sIdx] = { ...next[sIdx], licenses };
    next[sIdx].price = recomputePrice(next[sIdx]);
    setServices(next);
  };

  const removeLicense = (sIdx, lIdx) => {
    const next = [...services];
    next[sIdx] = { ...next[sIdx], licenses: next[sIdx].licenses.filter((_, i) => i !== lIdx) };
    if (next[sIdx].licenses.length > 0) next[sIdx].price = recomputePrice(next[sIdx]);
    setServices(next);
  };

  const updateNewLicType = (idx, field, value) => {
    setNewLicTypes({ ...newLicTypes, [idx]: { ...(newLicTypes[idx] || { name: "", price: "" }), [field]: value } });
  };

  const addNewLicenseType = async (idx) => {
    const entry = newLicTypes[idx] || {};
    const name = (entry.name || "").trim();
    if (!name) return;
    const price = entry.price || "";
    try {
      await billingAPI.addLicenseType({ name, default_price: parseFloat(price) || 0 });
      onCatalogRefresh();
      toast.success(`"${name}" saved to license types`);
    } catch (err) {
      // Already in catalog is fine — still add it as a license line
      if (!String(err.response?.data?.detail || "").includes("already exists")) {
        toast.error(err.response?.data?.detail || "Failed to save license type");
        return;
      }
    }
    const existingType = licenseTypes.find((t) => t.name.toLowerCase() === name.toLowerCase());
    const seatPrice = price || (existingType && existingType.default_price > 0 ? String(existingType.default_price) : "");
    const next = [...services];
    next[idx] = {
      ...next[idx],
      licenses: [...next[idx].licenses, { license_type_name: existingType?.name || name, quantity: "1", price_per_seat: seatPrice }],
    };
    next[idx].price = recomputePrice(next[idx]);
    setServices(next);
    setNewLicTypes({ ...newLicTypes, [idx]: { name: "", price: "" } });
  };

  const pushXeroUpdates = async () => {
    const initialByLine = {};
    (initialServices || []).forEach((s) => {
      if (s.xero_line_item_id) initialByLine[s.xero_line_item_id] = s;
    });
    const updates = services
      .filter((s) => s.xero_line_item_id && s.xero_repeating_invoice_id && s.licenses.length === 0)
      .filter((s) => {
        const init = initialByLine[s.xero_line_item_id];
        if (!init) return false;
        const initQty = parseFloat(init.quantity ?? 1);
        const initUnit = parseFloat(init.unit_price ?? init.price ?? 0);
        return (parseFloat(s.quantity) || 1) !== initQty || (parseFloat(s.unit_price) || 0) !== initUnit;
      })
      .map((s) => ({
        repeating_invoice_id: s.xero_repeating_invoice_id,
        line_item_id: s.xero_line_item_id,
        quantity: parseFloat(s.quantity) || 1,
        unit_amount: parseFloat(s.unit_price) || 0,
        service_name: s.service_name,
      }));
    if (updates.length === 0) return;
    try {
      const res = await xeroAPI.pushLineUpdates(updates);
      res.data.results.forEach((r) => {
        if (r.success) toast.success(`Xero updated: ${r.service_name}`);
        else toast.error(`Xero NOT updated — ${r.service_name}: ${r.error}`);
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to push changes to Xero");
    }
  };

  const handleSave = async () => {
    if (services.length === 0) {
      toast.error("Add at least one service before saving");
      return;
    }
    for (const s of services) {
      if (isOffice365(s.service_name)) {
        const invalid = s.licenses.some((l) => !l.license_type_name);
        if (invalid) {
          toast.error(`Select a license type for every license line in ${s.service_name}`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: customer.id,
        services: services.map((s) => ({
          service_name: s.service_name,
          price: parseFloat(s.price) || 0,
          quantity: parseFloat(s.quantity) || 1,
          unit_price: isNaN(parseFloat(s.unit_price)) ? null : parseFloat(s.unit_price),
          ots_cost: parseFloat(s.ots_cost) || 0,
          cost_frequency: s.cost_frequency || "monthly",
          frequency: s.frequency,
          renewal_date: s.renewal_date || null,
          xero_repeating_invoice_id: s.xero_repeating_invoice_id || null,
          xero_line_item_id: s.xero_line_item_id || null,
          licenses: s.licenses
            .filter((l) => l.license_type_name)
            .map((l) => ({
              license_type_name: l.license_type_name,
              quantity: parseInt(l.quantity) || 0,
              price_per_seat: parseFloat(l.price_per_seat) || 0,
            })),
        })),
      };
      const res = await billingAPI.saveProfile(payload);
      toast.success("Billing profile saved");
      await pushXeroUpdates();
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save billing profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-profile-editor">
      {/* Add services */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Add Services for {customer.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Select value={selectedCatalogService} onValueChange={setSelectedCatalogService}>
              <SelectTrigger className="flex-1" data-testid="editor-catalog-service-select">
                <SelectValue placeholder="Choose an existing service..." />
              </SelectTrigger>
              <SelectContent>
                {availableCatalog.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500">No more saved services</div>
                )}
                {availableCatalog.map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={addFromCatalog} disabled={!selectedCatalogService} data-testid="editor-add-catalog-service-button">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Or type a new service name (saved for next time)..."
              value={newServiceName}
              onChange={(e) => setNewServiceName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNewService()}
              data-testid="editor-new-service-input"
            />
            <Button variant="outline" onClick={addNewService} disabled={!newServiceName.trim()} data-testid="editor-add-new-service-button">
              <Plus className="w-4 h-4 mr-1" /> Add New
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Selected services */}
      {services.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500" data-testid="editor-no-services">
            No services selected yet. Add the services this customer uses above.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {services.map((svc, idx) => {
            const o365 = isOffice365(svc.service_name);
            return (
              <Card key={`${svc.service_name}-${idx}`} className="border-slate-200" data-testid={`editor-service-card-${idx}`}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{svc.service_name}</CardTitle>
                    {o365 && (
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                        <Monitor className="w-3 h-3 mr-1" /> Office 365
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeService(idx)}
                    data-testid={`editor-remove-service-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {o365 && (
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Licenses</Label>
                        <Button variant="outline" size="sm" onClick={() => addLicense(idx)} data-testid={`editor-add-license-${idx}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add License
                        </Button>
                      </div>
                      {svc.licenses.length === 0 && (
                        <p className="text-xs text-slate-500">Add the license types and number of seats for this customer.</p>
                      )}
                      {svc.licenses.map((lic, lIdx) => (
                        <div key={lIdx} className="flex gap-2 items-center">
                          <Select
                            value={lic.license_type_name}
                            onValueChange={(v) => updateLicense(idx, lIdx, "license_type_name", v)}
                          >
                            <SelectTrigger className="flex-1 bg-white" data-testid={`editor-license-type-select-${idx}-${lIdx}`}>
                              <SelectValue placeholder="License type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {licenseTypes.length === 0 && (
                                <div className="px-3 py-2 text-sm text-slate-500">
                                  No license types — add via "Manage Services"
                                </div>
                              )}
                              {licenseTypes.map((t) => (
                                <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="1"
                            className="w-20 bg-white"
                            placeholder="Qty"
                            value={lic.quantity}
                            onChange={(e) => updateLicense(idx, lIdx, "quantity", e.target.value)}
                            data-testid={`editor-license-qty-${idx}-${lIdx}`}
                          />
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-28 bg-white"
                            placeholder="$/seat"
                            value={lic.price_per_seat}
                            onChange={(e) => updateLicense(idx, lIdx, "price_per_seat", e.target.value)}
                            data-testid={`editor-license-price-${idx}-${lIdx}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                            onClick={() => removeLicense(idx, lIdx)}
                            data-testid={`editor-remove-license-${idx}-${lIdx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {svc.licenses.length > 0 && (
                        <p className="text-xs text-slate-600 text-right">
                          License total:{" "}
                          <span className="font-semibold">
                            {formatAUD(
                              svc.licenses.reduce(
                                (sum, l) => sum + (parseInt(l.quantity) || 0) * (parseFloat(l.price_per_seat) || 0),
                                0
                              )
                            )}
                          </span>
                        </p>
                      )}
                      <div className="flex gap-2 items-center pt-1 border-t border-slate-200">
                        <Input
                          placeholder="Or type a new license type (saved for next time)..."
                          className="flex-1 bg-white"
                          value={(newLicTypes[idx] || {}).name || ""}
                          onChange={(e) => updateNewLicType(idx, "name", e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && addNewLicenseType(idx)}
                          data-testid={`editor-new-license-type-name-${idx}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="$/seat"
                          className="w-28 bg-white"
                          value={(newLicTypes[idx] || {}).price || ""}
                          onChange={(e) => updateNewLicType(idx, "price", e.target.value)}
                          data-testid={`editor-new-license-type-price-${idx}`}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addNewLicenseType(idx)}
                          disabled={!((newLicTypes[idx] || {}).name || "").trim()}
                          data-testid={`editor-add-new-license-type-${idx}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Save & Add
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div className="lg:col-span-2">
                      <Label className="text-sm">Qty × Unit Price (AUD)</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Button type="button" variant="outline" size="sm" className="h-9 w-8 p-0 shrink-0" onClick={() => stepQty(idx, -1)} disabled={svc.licenses.length > 0 || (parseFloat(svc.quantity) || 1) <= 1} data-testid={`editor-service-qty-minus-${idx}`}>−</Button>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          className="w-16 text-center px-1"
                          value={svc.quantity}
                          onChange={(e) => updateService(idx, "quantity", e.target.value)}
                          disabled={svc.licenses.length > 0}
                          data-testid={`editor-service-quantity-${idx}`}
                        />
                        <Button type="button" variant="outline" size="sm" className="h-9 w-8 p-0 shrink-0" onClick={() => stepQty(idx, 1)} disabled={svc.licenses.length > 0} data-testid={`editor-service-qty-plus-${idx}`}>+</Button>
                        <span className="text-slate-400 text-sm">×</span>
                        <div className="relative flex-1 min-w-[90px]">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-7"
                            placeholder="0.00"
                            value={svc.unit_price}
                            onChange={(e) => updateService(idx, "unit_price", e.target.value)}
                            disabled={svc.licenses.length > 0}
                            data-testid={`editor-service-price-${idx}`}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1" data-testid={`editor-service-charge-${idx}`}>
                        Charge: <b className="text-slate-800">{formatAUD(parseFloat(svc.price) || 0)}</b>
                        {svc.xero_line_item_id && <span className="text-cyan-700"> · linked to Xero — changes push on save</span>}
                        {svc.licenses.length > 0 && <span> · from licenses below</span>}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm">Billing Frequency</Label>
                      <Select value={svc.frequency} onValueChange={(v) => updateService(idx, "frequency", v)}>
                        <SelectTrigger className="mt-1" data-testid={`editor-service-frequency-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map((f) => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">OTS Cost (AUD)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="pl-7"
                          placeholder="0.00"
                          value={svc.ots_cost}
                          onChange={(e) => updateService(idx, "ots_cost", e.target.value)}
                          data-testid={`editor-service-ots-cost-${idx}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Cost Frequency</Label>
                      <Select value={svc.cost_frequency} onValueChange={(v) => updateService(idx, "cost_frequency", v)}>
                        <SelectTrigger className="mt-1" data-testid={`editor-service-cost-frequency-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COST_FREQUENCIES.map((f) => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Next Renewal Date</Label>
                      <Input
                        type="date"
                        className="mt-1"
                        value={svc.renewal_date || ""}
                        onChange={(e) => updateService(idx, "renewal_date", e.target.value)}
                        data-testid={`editor-service-renewal-date-${idx}`}
                      />
                      <p className="text-[11px] text-slate-400 mt-1">
                        {svc.frequency === "monthly"
                          ? "Optional — sets the day shown in renewal lists"
                          : "Needed to show this service in monthly renewal lists"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel} data-testid="editor-cancel-button">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="editor-save-button">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Billing Profile"}
        </Button>
      </div>
    </div>
  );
}
